import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, BarChart3, Printer, Download, Send, RefreshCw, ArrowUpRight, FileText, CalendarRange } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { money, shortDate, dateInputValue } from "@/lib/format";
import { Card } from "@/components/ui-kit/Card";
import { Button } from "@/components/ui-kit/Button";
import { Input } from "@/components/ui-kit/Input";
import { Select } from "@/components/ui-kit/Select";
import { Table, THead, TBody, TR, TH, TD } from "@/components/ui-kit/Table";
import { Pill } from "@/components/ui-kit/Pill";
import { PdfPreviewDialog } from "@/components/pdf/PdfPreviewDialog";
import { cn } from "@/lib/cn";
import { useFunder } from "@/funder/FunderContext";

const TABS = [
  { id: "aging", label: "A/R Aging" },
  { id: "revenue", label: "Revenue & Collections" },
  { id: "unbilled", label: "Unbilled Work" },
];

function downloadCsv(filename, headers, rows) {
  const esc = (v) => {
    const s = v === null || v === undefined ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = [headers.map(esc).join(","), ...rows.map((r) => r.map(esc).join(","))].join("\n");
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
  const a = document.createElement("a");
  a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

function ToolbarButtons({ onCsv }) {
  return (
    <div className="flex items-center gap-2 print:hidden">
      <Button variant="secondary" size="sm" onClick={() => window.print()} data-testid="report-print">
        <Printer className="h-4 w-4" /> Print
      </Button>
      <Button variant="secondary" size="sm" onClick={onCsv} data-testid="report-csv">
        <Download className="h-4 w-4" /> CSV
      </Button>
    </div>
  );
}

/* ----------------------------- A/R Aging ----------------------------- */
function AgingReport({ lawpayConnected }) {
  const navigate = useNavigate();
  const { abbr, payerLabel } = useFunder();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [payer, setPayer] = useState("");
  const [bucket, setBucket] = useState("");
  const [minAge, setMinAge] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState("");
  const [stmtClient, setStmtClient] = useState(null);

  const load = () => {
    const params = {};
    if (payer) params.payer = payer;
    if (bucket) params.bucket = bucket;
    if (minAge) params.min_age = minAge;
    return api.get("/reports/aging", { params })
      .then((r) => setData(r.data))
      .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  };
  useEffect(() => { load(); }, [payer, bucket, minAge]);  

  const matterTo = (row) => row.matter_status === "Closed" ? `/matters/${row.matter_id}/record` : `/workbench/${row.matter_id}`;

  const remind = async (invoiceIds) => {
    if (!invoiceIds.length) return;
    setBusy(true); setFlash("");
    try {
      const r = await api.post("/reports/aging/remind", { invoice_ids: invoiceIds });
      setFlash(`Sent ${r.data.sent} reminder(s)${r.data.failed ? `, ${r.data.failed} failed` : ""}.`);
      setSelected(new Set());
      await load();
    } catch (e) {
      setFlash(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const reconcileAll = async () => {
    setBusy(true); setFlash("");
    try {
      const r = await api.post("/lawpay/reconcile-all");
      const { checked, recorded, total_amount, moved_to_paid, failures } = r.data;
      let msg = recorded > 0
        ? `Checked ${checked} invoice${checked === 1 ? "" : "s"}, recorded ${recorded} payment${recorded === 1 ? "" : "s"} (${money(total_amount)})${moved_to_paid ? `, ${moved_to_paid} now Paid` : ""}.`
        : `Checked ${checked} invoice${checked === 1 ? "" : "s"} — no new payments found.`;
      if (failures && failures.length) {
        msg += ` ${failures.length} failed: ${failures.map((f) => f.number || f.invoice_id).join(", ")}.`;
      }
      setFlash(msg);
      await load();
    } catch (e) {
      setFlash(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const toggle = (id) => setSelected((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  if (error) return <Card><div className="p-6 text-sm text-destructive">{error}</div></Card>;
  if (data === null) return <div className="grid place-items-center py-16"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>;

  const s = data.summary;
  const buckets = s.buckets;
  const clientRows = data.rows.filter((r) => r.payer === "Client");
  const over90 = clientRows.filter((r) => r.age_days >= 90).map((r) => r.invoice_id);

  const onCsv = () => downloadCsv("ar-aging.csv",
    ["Client", "Matter", "Payer", "Invoice", "Issued", "Age (days)", "Bucket", "Original", "Paid", "Balance due", "Last LawPay sent"],
    data.rows.map((r) => [r.client_name, r.matter_name, payerLabel(r.payer), r.number,
      shortDate(r.issue_date), r.age_days, r.bucket, r.amount, r.paid, r.balance_due,
      r.lawpay_sent_at ? shortDate(r.lawpay_sent_at) : ""]));

  return (
    <div className="space-y-6" data-testid="report-aging">
      {/* Summary */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
        <Card className="p-4">
          <p className="text-xs text-muted-foreground">Outstanding · Client</p>
          <p className="nums mt-1 text-xl font-semibold text-foreground" data-testid="aging-outstanding-client">{money(s.outstanding.client)}</p>
        </Card>
        <Card className="p-4">
          <p className="text-xs text-muted-foreground">Outstanding · {abbr}</p>
          <p className="nums mt-1 text-xl font-semibold text-foreground" data-testid="aging-outstanding-funder">{money(s.outstanding.funder)}</p>
        </Card>
        {buckets.map((b) => (
          <Card key={b.bucket} className="p-4">
            <p className="text-xs text-muted-foreground">{b.bucket} days <span className="text-muted-foreground/70">({b.count})</span></p>
            <p className="nums mt-1 text-xl font-semibold text-foreground" data-testid={`aging-bucket-${b.bucket}`}>{money(b.amount)}</p>
          </Card>
        ))}
      </div>

      {/* Filters + toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        <Select value={payer} onChange={(e) => setPayer(e.target.value)} data-testid="aging-payer-filter" className="max-w-[150px]">
          <option value="">Both payers</option>
          <option value="Client">Client</option>
          <option value="ACSA">{abbr}</option>
        </Select>
        <Select value={bucket} onChange={(e) => setBucket(e.target.value)} data-testid="aging-bucket-filter" className="max-w-[150px]">
          <option value="">All buckets</option>
          {["0-30", "31-60", "61-90", "90+"].map((b) => <option key={b} value={b}>{b} days</option>)}
        </Select>
        <Select value={minAge} onChange={(e) => setMinAge(e.target.value)} data-testid="aging-age-filter" className="max-w-[150px]">
          <option value="">Any age</option>
          <option value="31">31+ days</option>
          <option value="61">61+ days</option>
          <option value="90">90+ days</option>
        </Select>
        <div className="ml-auto flex items-center gap-2">
          {!lawpayConnected && <span className="text-xs text-muted-foreground print:hidden">Connect LawPay to send reminders</span>}
          <Button size="sm" variant="secondary" disabled={busy || !lawpayConnected} onClick={reconcileAll} data-testid="aging-reconcile-all">
            <RefreshCw className="h-4 w-4" /> Sync all from LawPay
          </Button>
          <Button size="sm" variant="secondary" disabled={busy || !lawpayConnected || selected.size === 0} onClick={() => remind([...selected])} data-testid="aging-remind-selected">
            <Send className="h-4 w-4" /> Send to selected ({selected.size})
          </Button>
          <Button size="sm" variant="secondary" disabled={busy || !lawpayConnected || over90.length === 0} onClick={() => remind(over90)} data-testid="aging-remind-90">
            Remind all 90+
          </Button>
          <ToolbarButtons onCsv={onCsv} />
        </div>
      </div>

      {flash && <p className="text-sm text-foreground" data-testid="aging-flash">{flash}</p>}

      <Card>
        {data.rows.length === 0 ? (
          <div className="py-16 text-center text-sm text-muted-foreground">Nothing outstanding — all caught up.</div>
        ) : (
          <Table>
            <THead>
              <TR>
                <TH className="w-8 print:hidden" />
                <TH>Client / Matter</TH><TH>Payer</TH><TH>Invoice</TH><TH>Issued</TH>
                <TH className="text-right">Age</TH><TH className="text-right">Original</TH>
                <TH className="text-right">Paid</TH><TH className="text-right">Balance</TH>
                <TH>Bucket</TH><TH className="print:hidden" />
              </TR>
            </THead>
            <TBody>
              {data.rows.map((r) => {
                const isClient = r.payer === "Client";
                return (
                  <TR key={r.invoice_id} data-testid={`aging-row-${r.invoice_id}`}>
                    <TD className="print:hidden">
                      {isClient && (
                        <input type="checkbox" checked={selected.has(r.invoice_id)} onChange={() => toggle(r.invoice_id)}
                          disabled={!lawpayConnected} data-testid={`aging-select-${r.invoice_id}`} className="h-4 w-4 accent-primary" />
                      )}
                    </TD>
                    <TD>
                      <button className="text-foreground hover:underline" onClick={() => navigate(`/clients-matters/${r.client_id}`)} data-testid={`aging-client-link-${r.invoice_id}`}>
                        {r.client_name}
                      </button>
                      {r.client_on_hold && <Pill tone="info" className="ml-2">On Hold</Pill>}
                      <button className="block text-xs text-muted-foreground hover:underline" onClick={() => navigate(matterTo(r))} data-testid={`aging-matter-link-${r.invoice_id}`}>
                        {r.matter_name}
                      </button>
                    </TD>
                    <TD className="text-muted-foreground">{payerLabel(r.payer)}</TD>
                    <TD className="text-muted-foreground">{r.number}</TD>
                    <TD className="whitespace-nowrap text-muted-foreground">{shortDate(r.issue_date)}</TD>
                    <TD className="nums text-right text-muted-foreground">{r.age_days}d</TD>
                    <TD className="nums text-right text-muted-foreground">{money(r.amount)}</TD>
                    <TD className="nums text-right text-muted-foreground">{money(r.paid)}</TD>
                    <TD className="nums text-right font-medium text-foreground">{money(r.balance_due)}</TD>
                    <TD><Pill tone={r.bucket === "90+" ? "accent" : r.bucket === "61-90" ? "info" : "muted"}>{r.bucket}</Pill></TD>
                    <TD className="text-right print:hidden">
                      <div className="flex items-center justify-end gap-1">
                        <Button size="sm" variant="ghost" onClick={() => setStmtClient({ id: r.client_id, name: r.client_name })} data-testid={`aging-statement-${r.invoice_id}`}>
                          <FileText className="h-3.5 w-3.5" /> Statement
                        </Button>
                        {isClient ? (
                          <Button size="sm" variant="ghost" disabled={busy || !lawpayConnected} onClick={() => remind([r.invoice_id])} data-testid={`aging-remind-${r.invoice_id}`}>
                            {r.lawpay_request_id ? <><RefreshCw className="h-3.5 w-3.5" /> Resend</> : <><Send className="h-3.5 w-3.5" /> Remind</>}
                          </Button>
                        ) : (
                          <span className="text-xs text-muted-foreground" data-testid={`aging-acsa-note-${r.invoice_id}`}>{abbr} email — coming soon</span>
                        )}
                      </div>
                    </TD>
                  </TR>
                );
              })}
            </TBody>
          </Table>
        )}
      </Card>

      <PdfPreviewDialog
        open={!!stmtClient}
        onOpenChange={(o) => { if (!o) setStmtClient(null); }}
        path={stmtClient ? `/clients/${stmtClient.id}/statement/pdf` : ""}
        title={stmtClient ? `Statement of Account — ${stmtClient.name}` : "Statement of Account"}
      />
    </div>
  );
}

/* ----------------------- Revenue & Collections ----------------------- */
function monthStart() {
  const d = new Date(); return dateInputValue(new Date(d.getFullYear(), d.getMonth(), 1).toISOString());
}

function RevenueReport() {
  const { abbr } = useFunder();
  const [start, setStart] = useState(monthStart());
  const [end, setEnd] = useState(dateInputValue(new Date().toISOString()));
  const [monthly, setMonthly] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const load = () => api.get("/reports/revenue", { params: { start, end, monthly } })
    .then((r) => setData(r.data))
    .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  useEffect(() => { load(); }, [start, end, monthly]);  

  if (error) return <Card><div className="p-6 text-sm text-destructive">{error}</div></Card>;

  const onCsv = () => {
    if (!data) return;
    const rows = [
      ["Collected — Client", "", data.collected.client],
      ["Collected — " + abbr, "", data.collected.funder],
      ["Collected — Total", "", data.collected.total],
      ["Billed — Client", "", data.billed.client],
      ["Billed — " + abbr, "", data.billed.funder],
      ["Billed — Total", "", data.billed.total],
    ];
    if (data.months) data.months.forEach((m) => rows.push([m.month, "collected C/" + abbr, `${m.collected_client}/${m.collected_funder}`]));
    downloadCsv("revenue-collections.csv", ["Metric", "Detail", "Amount"], rows);
  };

  return (
    <div className="space-y-6" data-testid="report-revenue">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="mb-1 block text-xs text-muted-foreground">From</label>
          <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="revenue-start" className="max-w-[170px]" />
        </div>
        <div>
          <label className="mb-1 block text-xs text-muted-foreground">To</label>
          <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="revenue-end" className="max-w-[170px]" />
        </div>
        <label className="flex items-center gap-2 text-sm text-muted-foreground">
          <input type="checkbox" checked={monthly} onChange={(e) => setMonthly(e.target.checked)} data-testid="revenue-monthly" className="h-4 w-4 accent-primary" />
          Month-by-month
        </label>
        <div className="ml-auto"><ToolbarButtons onCsv={onCsv} /></div>
      </div>

      {data === null ? (
        <div className="grid place-items-center py-16"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2">
            <Card className="p-5">
              <p className="text-sm font-medium text-foreground">Collected (payments received)</p>
              <div className="mt-3 space-y-1.5 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Client</span><span className="nums font-medium" data-testid="revenue-collected-client">{money(data.collected.client)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">{abbr}</span><span className="nums font-medium" data-testid="revenue-collected-funder">{money(data.collected.funder)}</span></div>
                <div className="flex justify-between border-t border-border pt-1.5"><span>Total</span><span className="nums font-semibold" data-testid="revenue-collected-total">{money(data.collected.total)}</span></div>
              </div>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {Object.entries(data.collected.by_method.client || {}).map(([k, v]) => <Pill key={"c" + k} tone="muted">{k}: {money(v)}</Pill>)}
                {Object.entries(data.collected.by_method.funder || {}).map(([k, v]) => <Pill key={"f" + k} tone="muted">{abbr} {k}: {money(v)}</Pill>)}
              </div>
            </Card>
            <Card className="p-5">
              <p className="text-sm font-medium text-foreground">Billed (incremental in range)</p>
              <div className="mt-3 space-y-1.5 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Client</span><span className="nums font-medium" data-testid="revenue-billed-client">{money(data.billed.client)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">{abbr}</span><span className="nums font-medium" data-testid="revenue-billed-funder">{money(data.billed.funder)}</span></div>
                <div className="flex justify-between border-t border-border pt-1.5"><span>Total</span><span className="nums font-semibold" data-testid="revenue-billed-total">{money(data.billed.total)}</span></div>
              </div>
            </Card>
          </div>

          {data.months && (
            <Card>
              <Table>
                <THead><TR><TH>Month</TH><TH className="text-right">Collected · Client</TH><TH className="text-right">Collected · {abbr}</TH><TH className="text-right">Billed · Client</TH><TH className="text-right">Billed · {abbr}</TH></TR></THead>
                <TBody>
                  {data.months.map((m) => (
                    <TR key={m.month} data-testid={`revenue-month-${m.month}`}>
                      <TD className="text-muted-foreground">{m.month}</TD>
                      <TD className="nums text-right">{money(m.collected_client)}</TD>
                      <TD className="nums text-right">{money(m.collected_funder)}</TD>
                      <TD className="nums text-right">{money(m.billed_client)}</TD>
                      <TD className="nums text-right">{money(m.billed_funder)}</TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

/* ----------------------------- Unbilled Work ----------------------------- */
function UnbilledReport() {
  const navigate = useNavigate();
  const [sort, setSort] = useState("value");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const load = () => api.get("/reports/unbilled", { params: { sort } })
    .then((r) => setData(r.data))
    .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  useEffect(() => { load(); }, [sort]);  

  if (error) return <Card><div className="p-6 text-sm text-destructive">{error}</div></Card>;

  const onCsv = () => downloadCsv("unbilled-work.csv",
    ["Client", "Matter", "Uninvoiced hours", "Value", "Last invoice", "Days since billed"],
    (data?.rows || []).map((r) => [r.client_name, r.matter_name, r.uninvoiced_hours, r.value,
      r.last_invoice_date ? shortDate(r.last_invoice_date) : "Never", r.days_since_billed ?? ""]));

  return (
    <div className="space-y-6" data-testid="report-unbilled">
      <div className="flex flex-wrap items-center gap-3">
        <Select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="unbilled-sort" className="max-w-[180px]">
          <option value="value">Sort by value</option>
          <option value="staleness">Sort by staleness</option>
        </Select>
        {data && <span className="text-sm text-muted-foreground">Total unbilled: <span className="nums font-semibold text-foreground" data-testid="unbilled-total">{money(data.total)}</span></span>}
        <div className="ml-auto"><ToolbarButtons onCsv={onCsv} /></div>
      </div>

      {data === null ? (
        <div className="grid place-items-center py-16"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
      ) : (
        <Card>
          {data.rows.length === 0 ? (
            <div className="py-16 text-center text-sm text-muted-foreground">No unbilled work — everything approved is invoiced.</div>
          ) : (
            <Table>
              <THead><TR><TH>Client / Matter</TH><TH className="text-right">Uninvoiced hours</TH><TH className="text-right">Value</TH><TH>Last invoice</TH><TH className="text-right">Idle</TH><TH className="print:hidden" /></TR></THead>
              <TBody>
                {data.rows.map((r) => (
                  <TR key={r.matter_id} data-testid={`unbilled-row-${r.matter_id}`}>
                    <TD>
                      <button className="text-foreground hover:underline" onClick={() => navigate(`/workbench/${r.matter_id}`)} data-testid={`unbilled-matter-link-${r.matter_id}`}>
                        {r.client_name} · {r.matter_name}
                      </button>
                    </TD>
                    <TD className="nums text-right text-muted-foreground">{r.uninvoiced_hours}h</TD>
                    <TD className="nums text-right font-medium text-foreground">{money(r.value)}</TD>
                    <TD className="text-muted-foreground">{r.last_invoice_date ? shortDate(r.last_invoice_date) : "Never billed"}</TD>
                    <TD className="nums text-right text-muted-foreground">{r.days_since_billed != null ? `${r.days_since_billed}d` : "—"}</TD>
                    <TD className="text-right print:hidden">
                      <Button size="sm" variant="ghost" onClick={() => navigate(`/workbench/${r.matter_id}`)} data-testid={`unbilled-bill-${r.matter_id}`}>
                        <ArrowUpRight className="h-3.5 w-3.5" /> Bill it
                      </Button>
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )}
        </Card>
      )}
    </div>
  );
}

export function ReportsPage() {
  const [tab, setTab] = useState("aging");
  const [lawpayConnected, setLawpayConnected] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/lawpay/status").then((r) => setLawpayConnected(!!r.data.send_connected)).catch(() => {});
  }, []);

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex flex-wrap items-center gap-3 print:hidden">
        <span className="grid h-10 w-10 place-items-center rounded-lg bg-primary/10 text-primary"><BarChart3 className="h-5 w-5" /></span>
        <div>
          <h1 className="font-serif text-3xl font-semibold tracking-tight text-foreground">Reports</h1>
          <p className="mt-1 text-sm text-muted-foreground">Read-only views over the validated ledger — A/R aging, revenue, and unbilled work.</p>
        </div>
        <Button variant="secondary" size="sm" className="ml-auto" onClick={() => navigate("/reports/year-end")} data-testid="open-year-end-report">
          <CalendarRange className="h-4 w-4" /> Year-End Report
        </Button>
      </div>

      <div className="flex gap-1 border-b border-border print:hidden">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`reports-tab-${t.id}`}
            className={cn("relative px-4 py-2.5 text-sm font-medium transition-colors",
              tab === t.id ? "text-foreground" : "text-muted-foreground hover:text-foreground")}>
            {t.label}
            {tab === t.id && <span className="absolute inset-x-0 -bottom-px h-0.5 bg-primary" />}
          </button>
        ))}
      </div>

      {tab === "aging" && <AgingReport lawpayConnected={lawpayConnected} />}
      {tab === "revenue" && <RevenueReport />}
      {tab === "unbilled" && <UnbilledReport />}
    </div>
  );
}

export default ReportsPage;
